import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { FUNCTION_RESULT_STATUS } from "@nova/common-interfaces";
import { IAssetSummary, ISubscription } from "@rfm/shared-interfaces";
import { BlockUIModule } from "ng-block-ui";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { AutoCompleteModule } from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { MenuModule } from "primeng/menu";
import { SelectButtonModule } from "primeng/selectbutton";
import { Table, TableModule } from "primeng/table";
import { TabMenuModule } from "primeng/tabmenu";
import { ToastModule } from "primeng/toast";
import { CommonComponent } from "../../../../../lib/components/index.mjs";
import { AssetService } from "../../../../shared/services/assets/asset.service";
import { SubscriptionService } from "../../../../shared/services/products/subscription.service.mjs";
import { ThirdPartyUispService } from "../../../../shared/services/third-party/stripe/third-party-uisp.service";

@Component({
  selector: "app-asset-list",
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    TableModule,
    BlockUIModule,
    AutoCompleteModule,
    ToastModule,
    ConfirmDialogModule,
    TabMenuModule,
    MenuModule,
    ButtonModule,
    SelectButtonModule,
    InputIconModule,
    IconFieldModule,
    InputTextModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: "./asset-list.component.html",
  styleUrls: ["./asset-list.component.scss"],
})
export class AssetListComponent extends CommonComponent implements OnInit {
  assets: IAssetSummary[] = [];
  filtered_assets: IAssetSummary[] = [];
  public override hasData: boolean = false;

  activeTab: any;
  activeItem: IAssetSummary | undefined;

  visible: boolean = false;

  showDialog() {
    this.visible = true;
  }

  selectedSubscription: ISubscription | undefined;
  selectedAsset: IAssetSummary | undefined;

  subscriptions: ISubscription[] = [];
  filteredSubscriptions: ISubscription[] = [];

  searchSubscriptions(event: any) {
    this.filteredSubscriptions = this.subscriptions.filter(
      (x) =>
        x.name?.toLowerCase().includes((event.query as string).toLowerCase()) ||
        x.customer_id.name
          ?.toLowerCase()
          .includes((event.query as string).toLowerCase())
    );
  }

  searchValue: string | undefined;

  clear(table: Table) {
    table.clear();
    this.searchValue = "";
  }

  filter(event: any, table: Table) {
    table.filterGlobal(event.target.value, "contains");
  }

  async assignAsset() {
    this.isLoading(true);

    console.log(`Attempting assignment for ${this.selectedAsset!.id}`);

    await this.assetService
      .assign(this.selectedAsset!.id, this.selectedSubscription!.id)
      .then((result) => {
        if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
          this.selectedSubscription = undefined;
          this.handleData(true);
          this.visible = false;
        }
      })
      .finally(() => {
        this.isLoading(false);
      });
  }

  tabs: MenuItem[] = [
    { label: "Online", command: () => this.setActiveTab("Online") },
    { label: "Offline", command: () => this.setActiveTab("Offline") },
    { label: "Active", command: () => this.setActiveTab("Active") },
    { label: "Disabled", command: () => this.setActiveTab("Disabled") },
    { label: "Disconnected", command: () => this.setActiveTab("Disconnected") },
    { label: "Deleted", command: () => this.setActiveTab("Deleted") },
    { label: "Unknown", command: () => this.setActiveTab("Unknown") },
  ];

  addAsset() {}

  setActiveTab(label: string) {
    this.activeTab = label;

    switch (label.toLowerCase()) {
      case "online":
        this.filtered_assets = this.assets.filter(
          (x) => x.status == "active" || x.status == "disconnected"
        );
        break;
      case "offline":
        this.filtered_assets = this.assets.filter(
          (x) => x.status == "disconnected"
        );
        break;
      case "active":
        this.filtered_assets = this.assets.filter((x) => x.status == "active");
        break;
      case "disabled":
        this.filtered_assets = this.assets.filter(
          (x) => x.status == "disabled"
        );
        break;
      case "disconnected":
        this.filtered_assets = this.assets.filter(
          (x) => x.status == "disconnected"
        );
        break;
      case "deleted":
        this.filtered_assets = this.assets.filter((x) => x.is_deleted == true);
        break;
      case "unknown":
        this.filtered_assets = this.assets.filter((x) => x.status == undefined);
        break;
    }
  }

  value: string = "off";

  buttonChanged(event: any) {
    this.setActiveTab("online");
  }

  items: MenuItem[] = [
    {
      label: "View",
      icon: "pi pi-file",
      command: () => this.view(this.activeItem!.id),
    },
    {
      label: "View Subscription",
      icon: "pi pi-eye",
      command: () => this.viewSubscription(this.activeItem!),
    },
    {
      label: "Edit",
      icon: "pi pi-file-edit",
      command: () => this.edit(this.activeItem!.id),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => this.delete(this.activeItem!.id),
    },
    {
      label: "Assign",
      icon: "pi pi-file-plus",
      command: () => this.assign(this.activeItem!.id),
    },
    {
      label: "Activate",
      icon: "pi pi-circle-on",
      command: () => this.activate(this.activeItem!.id),
    },
    {
      label: "Suspend",
      icon: "pi pi-circle-off",
      command: () => this.suspend(this.activeItem!.id),
    },
    {
      label: "Refresh",
      icon: "pi pi-refresh",
      command: () => this.refresh(this.activeItem!.id!),
    },
  ];

  async refresh(asset_id: string) {
    await this.assetService.refresh(asset_id).then((x) => {
      this.handleData(true);
    });
  }

  view(asset_id: string) {
    this.router.navigateByUrl("/admin/assets/view/" + asset_id);
  }

  viewSubscription(asset: IAssetSummary) {
    this.router.navigateByUrl(
      "/admin/subscriptions/view/" + asset.subscriptionId
    );
  }

  edit(asset_id: string) {
    this.router.navigateByUrl("/admin/assets/edit/" + asset_id);
  }

  delete(asset_id: string) {
    this.confirmationService.confirm({
      target: event!.target as EventTarget,
      message: "Are you sure that you want to delete this asset?",
      header: "Delete Asset",
      closable: true,
      closeOnEscape: true,
      icon: "pi pi-exclamation-triangle",
      rejectButtonProps: {
        label: "Cancel",
        severity: "secondary",
        outlined: true,
      },
      acceptButtonProps: {
        label: "Delete Asset",
      },
      accept: async () => {
        //*************************************
        //TODO: add workflow to credit and debit customer account for plan change
        // */
        this.isLoading(true);

        await this.assetService
          .remove(asset_id)
          .then((result) => {
            if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
              this.messageService.add({
                severity: "error",
                summary: "Asset was not deleted",
                detail: "Asset has not been changed",
                life: 3000,
              });
              this.isLoading(false);
              return Promise.reject(result);
            }

            this.messageService.add({
              severity: "success",
              summary: "Asset Deleted",
              detail: "Asset has been deleted successfully!",
              life: 3000,
            });
            this.isLoading(false);
            this.handleData(true);
            return Promise.resolve();
          })
          .catch((err: any) => {
            this.messageService.add({
              severity: "error",
              summary: "Asset Not Deleted",
              detail: "Asset has not been deleted",
              life: 3000,
            });
            this.isLoading(false);
            return Promise.reject(err);
          })
          .finally(() => {
            this.isLoading(false);
          });
      },
      reject: () => {
        this.isLoading(false);
      },
    });
  }

  async activate(asset_id: string) {
    this.isLoading(true);

    const assetResult = await this.assetService.get(asset_id);
   
    if (assetResult.status !== FUNCTION_RESULT_STATUS.SUCCESS)
    {
        alert("Unable to suspend device");
    }

    await this.uispService.activateDevice(assetResult.data!.integration!.id!).then((result) => {
      if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
        alert("Unable to suspend device");
      }
      this.isLoading(false);
    });
  }

  async suspend(asset_id: string) {
    this.isLoading(true);

    const assetResult = await this.assetService.get(asset_id);
   
    if (assetResult.status !== FUNCTION_RESULT_STATUS.SUCCESS)
    {
        alert("Unable to suspend device");
    }

    await this.uispService.suspendDevice(assetResult.data!.integration!.id!).then((result) => {
      if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
        alert("Unable to suspend device");
      }
      this.isLoading(false);
    });
  }

  async assign(asset_id: string) {
    this.isLoading(true);

    this.selectedAsset = this.assets.find((x) => x.id == asset_id);

    await this.subscriptionService.getAll().then((result) => {
      if (result.status !== FUNCTION_RESULT_STATUS.SUCCESS) {
        alert("Error");
      }

      this.subscriptions = result.data!;
      this.filteredSubscriptions = this.subscriptions;

      this.showDialog();
      this.isLoading(false);
    });
  }
  constructor(
    protected router: Router,
    protected assetService: AssetService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private subscriptionService: SubscriptionService,
    private uispService: ThirdPartyUispService
  ) {
    super();
    this.isLoading(true);
  }

  ngOnInit() {
    this.handleData(true);
  }

  handleData(state: boolean) {
    this.assetService
      .getSummary()
      .then((result) => {
        console.log("Asset result", result);
        if (result.status === FUNCTION_RESULT_STATUS.SUCCESS) {
          this.assets = result.data!;
          this.setActiveTab("online");
          this.hasData = true;
        }
      })
      .finally(() => {
        this.isLoading(false);
      });
  }
}
